import React, {ReactNode} from 'react';
import styled from 'styled-components';

import {BillingUpgradeIcon} from '../../../../libs/shared/billing/web/src/components/billingUpgradeIcon';
import {DropdownItemCheckmark} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckmark';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {DateRangePeriodsEnum, StartAndEndDatesWithPeriod} from '../../../models/dates/dateIntervalModel';
import {DateItemWrapperRendererType} from './dateRangeComboBox';

/*
 * Styles.
 */

const StyledLabelWrapperDiv = styled.div`
  display: flex;
`;

type PeriodRightContentDivProps = {
  $shouldHideLabel?: boolean;
};

export const PeriodRightContentDiv = styled.div<PeriodRightContentDivProps>`
  grid-area: right-icon;
  margin: 2px 0 0 20px;
  color: ${(p) => p.theme.greys.shade60};
  min-width: ${(p) => (p.$shouldHideLabel ? '50px' : '180px')};
  text-align: right;

  display: flex;
  justify-content: flex-end;
`;

const StyledPeriodLabel = styled.span`
  flex: 1;
  justify-self: flex-end;
}`;

const StyledPricingDiamondIcon = styled(BillingUpgradeIcon)`
  margin-left: 4px;
  margin-top: 2px;
`;

const StyledDropdownItemCheckmark = styled(DropdownItemCheckmark)`
  flex: 0;
`;

/*
 * Types.
 */

interface DateRangeComboBoxDropdownPeriodItemProps {
  label: ReactNode;
  period: StartAndEndDatesWithPeriod;
  onDateSelect: (period: StartAndEndDatesWithPeriod) => void;
  checkTimePeriodAccess?: (period: StartAndEndDatesWithPeriod) => void;
  dateRangeDropdownPeriodItemWrapperRenderer?: DateItemWrapperRendererType;
  hasCheckbox?: boolean;
  shouldHideLabel?: boolean;
  selectedPeriod?: DateRangePeriodsEnum;
}

/*
 * Component.
 */

export const DateRangeComboBoxDropdownPeriodItem = (props: DateRangeComboBoxDropdownPeriodItemProps) => {
  const {
    period,
    label,
    onDateSelect,
    checkTimePeriodAccess,
    dateRangeDropdownPeriodItemWrapperRenderer,
    hasCheckbox,
    selectedPeriod,
    shouldHideLabel,
  } = props;
  let format = 'MMM d, yyyy';
  let suffix = '';
  if (period.startDate?.year === period.endDate?.year) {
    format = 'MMM d';
    suffix = `, ${period.startDate?.year}`;
  }

  const periodLabel = `${period.startDate?.toFormat(format)}${
    period.endDate ? ` - ${period.endDate?.toFormat(format)}` : ''
  }`;

  const onSelect = () =>
    onDateSelect({
      startDate: period.startDate,
      endDate: period.endDate ?? period.startDate?.endOf('day'),
      period: period.period,
    });

  const isTimePeriodAvailable = checkTimePeriodAccess?.(period) ?? true;

  const isSelected = Boolean(selectedPeriod) && selectedPeriod === period.period;

  const simpleDropdownItem = (
    <SimpleDropdownItem
      checkmarkLabel={label}
      renderRightContent={() => (
        <PeriodRightContentDiv $shouldHideLabel={shouldHideLabel}>
          {!shouldHideLabel ? (
            <StyledPeriodLabel>
              {periodLabel}
              {suffix}
            </StyledPeriodLabel>
          ) : null}
          {hasCheckbox ? <StyledDropdownItemCheckmark isChecked={isSelected} /> : null}
        </PeriodRightContentDiv>
      )}
      onSelect={onSelect}
      isDisabled={!isTimePeriodAvailable}
      isChecked={isSelected}
    >
      <StyledLabelWrapperDiv>
        {label}
        {!isTimePeriodAvailable && <StyledPricingDiamondIcon />}
      </StyledLabelWrapperDiv>
    </SimpleDropdownItem>
  );

  if (dateRangeDropdownPeriodItemWrapperRenderer) {
    return <>{dateRangeDropdownPeriodItemWrapperRenderer(simpleDropdownItem, !isTimePeriodAvailable)}</>;
  }

  return simpleDropdownItem;
};
